/**
 * The departments a team member can belong to.
 *
 * Its own pane rather than a row in Lists, because it is the list the team
 * screen picks from and that is where somebody goes looking for it.
 *
 * The head count beside each one is read from the real accounts, so removing
 * or renaming a department shows what it is about to affect rather than
 * leaving people filed under something that no longer exists.
 */
import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { isOffline } from '../../lib/storage.js';
import api from '../../lib/api.js';
import Icon from '../../lib/icons.jsx';
import { EmptyState, Pill, Dash } from '../../components/Primitives.jsx';
import DepartmentSheet from '../../components/modals/DepartmentSheet.jsx';
import { Section } from './SettingsBits.jsx';

export default function DepartmentsPane() {
  const { S, updateSettings, toast } = useApp();
  const depts = S.depts || [];

  const [users, setUsers] = useState([]);
  const [sheet, setSheet] = useState(null);

  const offline = isOffline();

  const load = useCallback(async () => {
    if (offline) return;
    try { setUsers(await api.auth.users()); } catch { setUsers([]); }
  }, [offline]);

  useEffect(() => { load(); }, [load]);

  const countIn = dept => users.filter(u => (u.dept || '') === dept).length;

  /* Renaming moves everybody across with it; the list alone would leave them
     pointing at a department that is no longer offered. */
  const save = async next => {
    const previous = sheet && sheet.name;

    if (previous && !offline) {
      const moving = users.filter(u => (u.dept || '') === previous);
      for (const u of moving) await api.auth.updateUser(u.id, { dept: next });
    }

    updateSettings(s => {
      const list = s.depts || [];
      s.depts = previous
        ? list.map(d => (d === previous ? next : d))
        : [...list, next];
    });

    await load();
    toast(previous ? `Renamed to ${next}` : `${next} added`);
  };

  const remove = async dept => {
    const n = countIn(dept);
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      n
        ? `Remove ${dept}?\n\n${n} ${n === 1 ? 'person keeps' : 'people keep'} it on their record — `
          + 'it simply stops being offered for new ones.'
        : `Remove ${dept}?`
    )) return;

    updateSettings(s => { s.depts = (s.depts || []).filter(d => d !== dept); });
    toast(`${dept} removed`);
  };

  return (
    <>
      <Section
        title="Departments"
        hint="Added here, they appear in the department picker when you add or edit a team member."
        action={
          <button type="button" className="btn primary" onClick={() => setSheet({ name: '' })}>
            <Icon name="plus" />Add a department
          </button>
        }
      >
        {depts.length ? (
          <div className="tscroll">
            <table>
              <thead>
                <tr><th>Department</th><th>People</th><th /></tr>
              </thead>
              <tbody>
                {depts.map(d => {
                  const n = countIn(d);
                  return (
                    <tr key={d}>
                      <td><b>{d}</b></td>
                      <td>
                        {offline
                          ? <Dash />
                          : n
                            ? <Pill tone="p-blue">{n === 1 ? '1 person' : `${n} people`}</Pill>
                            : <span className="dim">Nobody yet</span>}
                      </td>
                      <td>
                        <div className="acts">
                          <button
                            type="button"
                            title="Rename"
                            onClick={() => setSheet({ name: d, count: n })}
                          >
                            <Icon name="edit" />
                          </button>
                          <button
                            type="button"
                            className="rm"
                            title="Remove"
                            onClick={() => remove(d)}
                          >
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="building" tone="g-cyan" title="No departments yet">
            Add one and it becomes a choice on every team member.
          </EmptyState>
        )}
      </Section>

      {sheet ? (
        <DepartmentSheet
          key={sheet.name || 'new'}
          value={sheet.name || ''}
          count={sheet.count || 0}
          onClose={() => setSheet(null)}
          onSaved={save}
        />
      ) : null}
    </>
  );
}
